import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useToggle } from 'react-use'

import { Icon } from '@iconify/react'

import { Button } from 'common/components/Button/Button'
import { Table } from 'common/components/Table/Table'
import { BUTTON_PROPORTION, BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { DEFAULT_TABLE_PARAMS } from 'common/constants/defaultTableParamsConstants'
import type { TableParamsType } from 'common/interfaces/ITable'
import type { ObjectType } from 'common/interfaces/object'

import { ManageProduct } from 'features/Procedure/features/treatments/components/manage-product/manage-product'
import type { ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import { useFetchProductsQuery } from 'features/Procedure/state/procedure.api'

import { productsTableColumns } from './columns'
import { historicalTableColumns, type HistoricalTableRow } from './historicalColumns'
import styles from './products.module.scss'

export type ProductsProps = {
  onRegisterAction?: (action: ReactNode | null) => void
  selectedEncounterUid: string | null
  disableActions?: boolean
  className?: string
}

export const Products = ({
  onRegisterAction,
  selectedEncounterUid,
  disableActions = false,
  className,
}: ProductsProps) => {
  const [columns, setColumns] = useState<ObjectType[]>([])
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS,
  })
  const [historicalColumns, setHistoricalColumns] = useState<ObjectType[]>([])
  const [historicalTableParams, setHistoricalTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS,
  })
  const [isVisibleManageProduct, toggleManageProduct] = useToggle(false)
  const [selectedProduct, setSelectedProduct] = useState<ProcedureItem | null>(null)
  const [forceAddMode, setForceAddMode] = useState(false)
  const { siteId } = useParams()

  const { data: productsData, isFetching } = useFetchProductsQuery(
    {
      siteId,
      encounterUid: selectedEncounterUid,
    },
    {
      skip: !selectedEncounterUid,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  const items = useMemo(() => {
    if (!productsData?.data?.items) return []
    return productsData.data.items
  }, [productsData])

  const historicalItems = useMemo(() => {
    if (!productsData?.historical_data?.items) {
      return []
    }
    const flattened: (HistoricalTableRow & { uniqueKey: string })[] = []
    productsData.historical_data.items.forEach((encounter) => {
      if (encounter.items && encounter.items.length > 0) {
        encounter.items.forEach((item) => {
          flattened.push({
            ...item,
            encounter_date: encounter.encounter_date,
            uniqueKey: `${encounter.encounter_id}-${item.id}`,
          })
        })
      }
    })
    return flattened
  }, [productsData])

  useEffect(() => {
    if (productsData?.data?.items) {
      setTableParams((prev) => ({ ...prev, total: productsData.data.items.length }))
    }
  }, [productsData])

  useEffect(() => {
    if (historicalItems.length > 0) {
      setHistoricalTableParams((prev) => ({ ...prev, total: historicalItems.length }))
    }
  }, [historicalItems])

  const handleProductClick = useCallback(
    (product: ProcedureItem) => {
      if (disableActions) return
      setSelectedProduct(product)
      setForceAddMode(false)
      toggleManageProduct(true)
    },
    [toggleManageProduct, disableActions],
  )

  const handleHistoricalProductClick = useCallback(
    (product: HistoricalTableRow) => {
      const procedureItem: ProcedureItem = {
        id: product.id,
        code_type: product.code_type,
        code: product.code,
        code_text: product.code_text,
        procedure_units: product.procedure_units,
        units: product.units,
        fee: product.fee,
        total_price: product.total_price,
        authorized: Boolean(product.authorized),
        provider_id: product.provider_id,
        notecodes: product.notecodes,
        billed: product.billed === 1 ? 1 : 0,
        pricelevel: product.pricelevel,
        revenue_code: product.revenue_code,
        justify_codes: product.justify_codes,
        drawings: {} as any,
        product_labels:
          typeof product.product_labels === 'string'
            ? product.product_labels === '[]' || product.product_labels === ''
              ? []
              : JSON.parse(product.product_labels)
            : product.product_labels || [],
      }
      setSelectedProduct(procedureItem)
      setForceAddMode(true)
      toggleManageProduct(true)
    },
    [toggleManageProduct],
  )

  const handleAddProducts = useCallback(() => {
    setSelectedProduct(null)
    setForceAddMode(false)
    toggleManageProduct(true)
  }, [toggleManageProduct])

  const handleCloseManageProduct = useCallback(() => {
    setSelectedProduct(null)
    setForceAddMode(false)
    toggleManageProduct(false)
  }, [toggleManageProduct])

  useEffect(() => {
    setColumns(productsTableColumns(handleProductClick))
    setHistoricalColumns(historicalTableColumns(handleHistoricalProductClick))
  }, [handleProductClick, handleHistoricalProductClick])

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: tableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    if (historicalTableParams.sorters) {
      setHistoricalColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: historicalTableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [historicalTableParams.sorters])

  useEffect(() => {
    if (!onRegisterAction) return
    onRegisterAction(
      <Button
        proportion={BUTTON_PROPORTION.SMALL}
        onClick={handleAddProducts}
        severity={BUTTON_SEVERITY.SUCCESS_FILLED}
        disabled={!selectedEncounterUid || disableActions}
        icon={
          <div className={styles.iconWrapper}>
            <Icon icon='ph:plus' width={12} height={12} />
          </div>
        }>
        Add Product
      </Button>,
    )
    return () => {
      onRegisterAction(null)
    }
  }, [handleAddProducts, onRegisterAction, selectedEncounterUid, disableActions])

  return (
    <>
      <ManageProduct
        isVisible={isVisibleManageProduct}
        onClose={handleCloseManageProduct}
        procedure={selectedProduct}
        forceAddMode={forceAddMode}
        selectedEncounterUid={selectedEncounterUid}
      />
      <Table
        itemKey='id'
        tableParams={tableParams}
        dataSource={items}
        setTableParams={setTableParams}
        columns={columns}
        isLoading={isFetching}
        haveActions={false}
        pagination={false}
        className={className}
      />

      {!disableActions && historicalItems.length > 0 && (
        <div className={styles.historicalSection}>
          <h3 className={styles.historicalTitle}>Recent purchases</h3>
          <Table
            itemKey='uniqueKey'
            tableParams={historicalTableParams}
            dataSource={historicalItems}
            setTableParams={setHistoricalTableParams}
            columns={historicalColumns}
            isLoading={isFetching}
            haveActions={false}
            pagination={false}
          />
        </div>
      )}
    </>
  )
}
